import { ref, type Ref } from 'vue'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'

export interface CustomPaymentMethod {
  id: string
  name: string
  enabled: boolean
}

export interface PaymentMethodsConfig {
  cash: boolean
  card: boolean
  digital: boolean
  other: boolean
  custom_methods: CustomPaymentMethod[]
}

export interface RestaurantSettings {
  advanced_printing_enabled: boolean
  kitchen_print_enabled: boolean
  kitchen_print_paper_width: number
  customer_print_enabled: boolean
  customer_print_paper_width: number
  allow_dine_in_without_table: boolean
  allow_takeaway_without_customer: boolean
  payment_methods_config: PaymentMethodsConfig
  iva_enabled: boolean
  iva_rate: number
}

export interface ISettingsService {
  updateSetting(key: keyof RestaurantSettings, value: any): Promise<void>
  loadSettings(): Promise<Partial<RestaurantSettings>>
}

class RestaurantSettingsService implements ISettingsService {
  async updateSetting(key: keyof RestaurantSettings, value: any): Promise<void> {
    await api.patch('/restaurants/current', { [key]: value })
  }

  async loadSettings(): Promise<Partial<RestaurantSettings>> {
    const authStore = useAuthStore()
    try {
      // Try to load restaurant data, but don't let it block everything
      await authStore.loadRestaurant()
    } catch (error) {
      console.warn('Could not load restaurant data, using defaults:', error)
    }
    return authStore.restaurant || {}
  }
}

export function useRestaurantSettings(settingsService: ISettingsService = new RestaurantSettingsService()) {
  const toast = useToast()
  const { t } = useI18n()
  const authStore = useAuthStore()

  // Reactive state
  const advancedPrintingEnabled = ref(false)
  const kitchenPrintEnabled = ref(true)
  const paperWidth = ref(80)
  const customerPrintEnabled = ref(true)
  const customerPaperWidth = ref(80)
  const allowDineInWithoutTable = ref(false)
  const allowTakeawayWithoutCustomer = ref(false)
  const paymentMethodsConfig = ref<PaymentMethodsConfig>({
    cash: true,
    card: false,
    digital: true,
    other: false,
    custom_methods: []
  })
  const ivaEnabled = ref(false)
  const ivaRate = ref(0.16)
  const savingSettings = ref(false)
  const loadingSettings = ref(false)

  // Settings management methods
  const updateSetting = async <K extends keyof RestaurantSettings>(
    key: K,
    value: RestaurantSettings[K],
    successMessage: string
  ): Promise<void> => {
    try {
      savingSettings.value = true

      // Update backend
      await settingsService.updateSetting(key, value)

      // Update local state
      switch (key) {
        case 'advanced_printing_enabled':
          advancedPrintingEnabled.value = value as boolean
          break
        case 'kitchen_print_enabled':
          kitchenPrintEnabled.value = value as boolean
          break
        case 'kitchen_print_paper_width':
          paperWidth.value = value as number
          break
        case 'customer_print_enabled':
          customerPrintEnabled.value = value as boolean
          break
        case 'customer_print_paper_width':
          customerPaperWidth.value = value as number
          break
        case 'allow_dine_in_without_table':
          allowDineInWithoutTable.value = value as boolean
          break
        case 'allow_takeaway_without_customer':
          allowTakeawayWithoutCustomer.value = value as boolean
          break
        case 'payment_methods_config':
          paymentMethodsConfig.value = value as PaymentMethodsConfig
          break
        case 'iva_enabled':
          ivaEnabled.value = value as boolean
          break
        case 'iva_rate':
          ivaRate.value = value as number
          break
      }

      // Update auth store
      if (authStore.restaurant) {
        authStore.restaurant[key] = value
      }

      // For IVA settings, reload restaurant data to ensure consistency
      if (key === 'iva_enabled' || key === 'iva_rate') {
        await authStore.loadRestaurant()
      }

      // Show success toast
      toast.success(successMessage)
    } catch (error) {
      console.error(`Error updating ${key} setting:`, error)
      toast.error(t('app.subscription.settings.error'))
    } finally {
      savingSettings.value = false
    }
  }

  // Specific toggle methods with proper typing
  const toggleAdvancedPrinting = (): Promise<void> => {
    const newValue = !advancedPrintingEnabled.value
    const message = newValue 
      ? 'Impresión avanzada activada'
      : 'Impresión avanzada desactivada'
    
    return updateSetting('advanced_printing_enabled', newValue, message)
  }

  const toggleKitchenPrint = (): Promise<void> => {
    const newValue = !kitchenPrintEnabled.value
    const message = newValue 
      ? t('app.subscription.settings.kitchen_print.enabled')
      : t('app.subscription.settings.kitchen_print.disabled')
    
    return updateSetting('kitchen_print_enabled', newValue, message)
  }

  const setPaperWidth = (width: number): Promise<void> => {
    const message = t('app.subscription.settings.paper_width.updated', { width: `${width}mm` })
    return updateSetting('kitchen_print_paper_width', width, message)
  }

  const toggleCustomerPrint = (): Promise<void> => {
    const newValue = !customerPrintEnabled.value
    const message = newValue 
      ? t('app.subscription.settings.customer_print.enabled')
      : t('app.subscription.settings.customer_print.disabled')
    
    return updateSetting('customer_print_enabled', newValue, message)
  }

  const setCustomerPaperWidth = (width: number): Promise<void> => {
    const message = t('app.subscription.settings.customer_paper_width.updated', { width: `${width}mm` })
    return updateSetting('customer_print_paper_width', width, message)
  }

  const toggleDineInWithoutTable = (): Promise<void> => {
    const newValue = !allowDineInWithoutTable.value
    const message = newValue 
      ? t('app.subscription.settings.dine_in_without_table.enabled')
      : t('app.subscription.settings.dine_in_without_table.disabled')
    
    return updateSetting('allow_dine_in_without_table', newValue, message)
  }

  const toggleTakeawayWithoutCustomer = (): Promise<void> => {
    const newValue = !allowTakeawayWithoutCustomer.value
    const message = newValue 
      ? t('app.subscription.settings.takeaway_without_customer.enabled')
      : t('app.subscription.settings.takeaway_without_customer.disabled')
    
    return updateSetting('allow_takeaway_without_customer', newValue, message)
  }

  const togglePaymentMethod = (method: 'cash' | 'card' | 'digital' | 'other'): Promise<void> => {
    // Cash cannot be disabled
    if (method === 'cash') {
      toast.warning(t('app.subscription.settings.payment_methods.cash_always_enabled'))
      return Promise.resolve()
    }

    const newConfig = { ...paymentMethodsConfig.value }
    newConfig[method] = !newConfig[method]
    
    const message = newConfig[method]
      ? t('app.subscription.settings.payment_methods.enabled', { method: t(`app.subscription.settings.payment_methods.${method}`) })
      : t('app.subscription.settings.payment_methods.disabled', { method: t(`app.subscription.settings.payment_methods.${method}`) })
    
    return updateSetting('payment_methods_config', newConfig, message)
  }

  const toggleIvaEnabled = (): Promise<void> => {
    const newValue = !ivaEnabled.value
    const message = newValue 
      ? 'IVA habilitado'
      : 'IVA deshabilitado'
    
    return updateSetting('iva_enabled', newValue, message)
  }

  const updateIvaRate = (rate: number): Promise<void> => {
    const message = `Tasa de IVA actualizada al ${(rate * 100).toFixed(0)}%`
    return updateSetting('iva_rate', rate, message)
  }

  // Custom payment methods management
  const addCustomPaymentMethod = async (name: string): Promise<void> => {
    if ((paymentMethodsConfig.value.custom_methods || []).length >= 3) {
      toast.warning('Máximo 3 métodos de pago personalizados permitidos')
      return
    }

    if (!name.trim()) {
      toast.error('El nombre del método de pago es requerido')
      return
    }

    const newCustomMethod: CustomPaymentMethod = {
      id: Date.now().toString(),
      name: name.trim(),
      enabled: true
    }

    const newConfig = { ...paymentMethodsConfig.value }
    newConfig.custom_methods = [...(newConfig.custom_methods || []), newCustomMethod]
    
    const message = `Método de pago "${name}" agregado`
    return updateSetting('payment_methods_config', newConfig, message)
  }

  const removeCustomPaymentMethod = async (name: string): Promise<void> => {
    const methodToRemove = (paymentMethodsConfig.value.custom_methods || []).find(m => m.name === name)
    if (!methodToRemove) return

    const newConfig = { ...paymentMethodsConfig.value }
    newConfig.custom_methods = (newConfig.custom_methods || []).filter(m => m.name !== name)
    
    const message = `Método de pago "${methodToRemove.name}" eliminado`
    return updateSetting('payment_methods_config', newConfig, message)
  }

  const toggleCustomPaymentMethod = async (name: string): Promise<void> => {
    const method = (paymentMethodsConfig.value.custom_methods || []).find(m => m.name === name)
    if (!method) return

    const newConfig = { ...paymentMethodsConfig.value }
    newConfig.custom_methods = (newConfig.custom_methods || []).map(m => 
      m.name === name ? { ...m, enabled: !m.enabled } : m
    )
    
    const message = `Método de pago "${method.name}" ${method.enabled ? 'desactivado' : 'activado'}`
    return updateSetting('payment_methods_config', newConfig, message)
  }

  const updateCustomPaymentMethodName = async (oldName: string, newName: string): Promise<void> => {
    if (!newName.trim()) {
      toast.error('El nombre del método de pago es requerido')
      return
    }

    const method = (paymentMethodsConfig.value.custom_methods || []).find(m => m.name === oldName)
    if (!method) return

    const newConfig = { ...paymentMethodsConfig.value }
    newConfig.custom_methods = (newConfig.custom_methods || []).map(m => 
      m.name === oldName ? { ...m, name: newName.trim() } : m
    )
    
    const message = `Método de pago renombrado a "${newName.trim()}"`
    return updateSetting('payment_methods_config', newConfig, message)
  }

  // Load settings from backend
  const loadSettings = async (): Promise<void> => {
    try {
      loadingSettings.value = true
      
      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Loading timeout')), 10000)
      })
      
      const settings = await Promise.race([
        settingsService.loadSettings(),
        timeoutPromise
      ]) as Partial<RestaurantSettings>
      
      advancedPrintingEnabled.value = settings.advanced_printing_enabled ?? false
      kitchenPrintEnabled.value = settings.kitchen_print_enabled ?? true
      paperWidth.value = settings.kitchen_print_paper_width ?? 80
      customerPrintEnabled.value = settings.customer_print_enabled ?? true
      customerPaperWidth.value = settings.customer_print_paper_width ?? 80
      allowDineInWithoutTable.value = settings.allow_dine_in_without_table ?? false
      allowTakeawayWithoutCustomer.value = settings.allow_takeaway_without_customer ?? false
      paymentMethodsConfig.value = settings.payment_methods_config ?? {
        cash: true,
        card: false,
        digital: true,
        other: false,
        custom_methods: []
      }
      ivaEnabled.value = settings.iva_enabled ?? false
      ivaRate.value = settings.iva_rate ?? 0.16
    } catch (error) {
      console.error('Error loading restaurant settings:', error)
      toast.error(t('app.subscription.settings.error'))
      // Set default values on error to prevent UI from breaking
      paymentMethodsConfig.value = {
        cash: true,
        card: false,
        digital: true,
        other: false,
        custom_methods: []
      }
    } finally {
      loadingSettings.value = false
    }
  }

  return {
    // State
    advancedPrintingEnabled: advancedPrintingEnabled as Ref<boolean>,
    kitchenPrintEnabled: kitchenPrintEnabled as Ref<boolean>,
    paperWidth: paperWidth as Ref<number>,
    customerPrintEnabled: customerPrintEnabled as Ref<boolean>,
    customerPaperWidth: customerPaperWidth as Ref<number>,
    allowDineInWithoutTable: allowDineInWithoutTable as Ref<boolean>,
    allowTakeawayWithoutCustomer: allowTakeawayWithoutCustomer as Ref<boolean>,
    paymentMethodsConfig: paymentMethodsConfig as Ref<PaymentMethodsConfig>,
    ivaEnabled: ivaEnabled as Ref<boolean>,
    ivaRate: ivaRate as Ref<number>,
    savingSettings: savingSettings as Ref<boolean>,
    loadingSettings: loadingSettings as Ref<boolean>,
    
    // Methods
    toggleAdvancedPrinting,
    toggleKitchenPrint,
    setPaperWidth,
    toggleCustomerPrint,
    setCustomerPaperWidth,
    toggleDineInWithoutTable,
    toggleTakeawayWithoutCustomer,
    togglePaymentMethod,
    toggleIvaEnabled,
    updateIvaRate,
    loadSettings,
    
    // Custom payment methods
    addCustomPaymentMethod,
    removeCustomPaymentMethod,
    toggleCustomPaymentMethod,
    updateCustomPaymentMethodName,
    
    // Service injection (for testing)
    settingsService
  }
}
